import { getApiConfig } from '../config'
import { download, fetchDelete, get, patch, post } from './http-utils'
import {
  AcceptDraftResponse,
  AuthenticatedHttpRequest,
  Category,
  ClientInfo,
  Company,
  CouponsLimit,
  CouponsStatus,
  CustomerPreference,
  Employee,
  EmployeeTripsResponse,
  EmploymentEndCause,
  EmploymentType,
  FamilyMember,
  FipTicket,
  HttpRequest,
  HttpResponse,
  ManualOrder,
  Page,
  PayType,
  PostalCode,
  SensitiveEmployeeInfo,
  SimpleRule,
  StationPair,
  StopPlaces,
  TicketRight,
  TicketRightHolder,
  TicketType,
  YearlyCategory,
  YearlyDisabilityProof,
  YearlyFee,
  YearlyLowIncome,
} from './api-types'

const baseUrl = `${getApiConfig().url}/personnelticket/v2`

const emptyPage = <T>() => {
  const page: Page<T> = {
    items: [],
    totalItems: 0,
    totalPages: 0,
  }
  return Promise.resolve({ json: page } as HttpResponse<Page<T>>)
}

export function getClientInfo(request: AuthenticatedHttpRequest): Promise<HttpResponse<ClientInfo>> {
  return get<ClientInfo>(`${baseUrl}/eba/client-info`, request.token)
}

export function getRules(request: AuthenticatedHttpRequest): Promise<HttpResponse<Array<SimpleRule>>> {
  return get<Array<SimpleRule>>(`${baseUrl}/eba/rules`, request.token)
}

export function getPossibleCompanies(request: AuthenticatedHttpRequest): Promise<HttpResponse<Page<Company>>> {
  return get<Page<Company>>(`${baseUrl}/companies`, request.token)
}

export function getProducts(request: AuthenticatedHttpRequest): Promise<HttpResponse<Array<Category>>> {
  return get<Array<Category>>(`${baseUrl}/companies/${request.id}/products`, request.token)
}

export function getTicketTypes(request: AuthenticatedHttpRequest): Promise<HttpResponse<Page<TicketType>>> {
  return get<Page<TicketType>>(`${baseUrl}/code-pages/ticket-types?generatesTicketRights=true`, request.token)
}

export function getFIPTicketTypes(request: AuthenticatedHttpRequest): Promise<HttpResponse<Page<TicketType>>> {
  return get<Page<TicketType>>(
    `${baseUrl}/code-pages/ticket-types?perPage=150&generatesTicketRights=false`,
    request.token
  )
}

export function getEmploymentTypes(
  request: AuthenticatedHttpRequest
): Promise<HttpResponse<Array<EmploymentType>>> {
  return get<Array<EmploymentType>>(`${baseUrl}/code-pages/employment-types`, request.token)
}

export function getEmploymentEndCauses(
  request: AuthenticatedHttpRequest
): Promise<HttpResponse<Array<EmploymentEndCause>>> {
  return get<Array<EmploymentEndCause>>(`${baseUrl}/code-pages/employment-end-causes`, request.token)
}

export function getPaymentTypes(request: AuthenticatedHttpRequest): Promise<HttpResponse<Array<PayType>>> {
  return get<Array<PayType>>(`${baseUrl}/code-pages/pay-types`, request.token)
}

export function getTicketRightHolders(
  request: AuthenticatedHttpRequest
): Promise<HttpResponse<Page<TicketRightHolder>>> {
  if (request.query) {
    return get<Page<TicketRightHolder>>(`${baseUrl}/eba/ticket-right-holders${request.query}`, request.token)
  }
  return emptyPage<TicketRightHolder>()
}

export function getEmployee(request: AuthenticatedHttpRequest) {
  return get<Employee>(
    `${baseUrl}/eba/employees/${request.id}?includeTicketRights=true&includePreferences=true`,
    request.token
  )
}

export function getSensitiveEmployeeFields(request: AuthenticatedHttpRequest) {
  return get<SensitiveEmployeeInfo>(`${baseUrl}/eba/employees/${request.id}/sensitive-data`, request.token)
}

export function patchEmployee(request: AuthenticatedHttpRequest) {
  return patch<Employee>(`${baseUrl}/eba/employees/${request.id}`, request.token, request.body || {})
}

export function postEmployee(request: AuthenticatedHttpRequest) {
  return post<Employee>(`${baseUrl}/eba/employees`, request.token, request.body || {})
}

export function postRestoreEmployee(request: AuthenticatedHttpRequest) {
  return post<Employee>(`${baseUrl}/eba/employees/${request.id}/restore`, request.token, {})
}

export function postAcceptDraft(request: AuthenticatedHttpRequest) {
  return post<AcceptDraftResponse>(
    `${baseUrl}/eba/employees/${request.id}/accept-draft`,
    request.token,
    request.body || {}
  )
}

export function postRejectDraft(request: AuthenticatedHttpRequest) {
  return post(`${baseUrl}/eba/employees/${request.id}/reject-draft`, request.token, {})
}

export function patchCustomerPreference(request: AuthenticatedHttpRequest) {
  return patch<CustomerPreference>(`${baseUrl}/eba/customers/${request.id}`, request.token, request.body || {})
}

export function patchSensitiveEmployeeData(request: AuthenticatedHttpRequest) {
  return patch<SensitiveEmployeeInfo>(
    `${baseUrl}/eba/employees/${request.id}/sensitive-data`,
    request.token,
    request.body || {}
  )
}

export function postTicketRights(request: AuthenticatedHttpRequest) {
  return post<Array<TicketRight>>(
    `${baseUrl}/eba/employees/${request.id}/ticket-rights`,
    request.token,
    request.body || {}
  )
}

export function postSyncTicketRight(request: AuthenticatedHttpRequest) {
  return post<TicketRight>(`${baseUrl}/eba/ticket-rights/${request.id}/sync`, request.token, request.body || {})
}

export function getTicketRights(request: AuthenticatedHttpRequest) {
  return get<Array<TicketRight>>(`${baseUrl}/eba/employees/${request.id}/ticket-rights`, request.token)
}

export function getFIPTickets(request: AuthenticatedHttpRequest) {
  return get<Array<FipTicket>>(`${baseUrl}/eba/employees/${request.id}/fip-tickets`, request.token)
}

export function getFIPTicketsReport(request: AuthenticatedHttpRequest): Promise<HttpResponse<undefined>> {
  return download(`${baseUrl}/reports/fip-monthly${request.query}`, request.token)
}

export function getStopPlaceTicketRightsReport(
  request: AuthenticatedHttpRequest
): Promise<HttpResponse<undefined>> {
  return download(`${baseUrl}/reports/stop-place-ticket-rights${request.query}`, request.token)
}

export function getApprenticeTicketRightsReport(
  request: AuthenticatedHttpRequest
): Promise<HttpResponse<undefined>> {
  return download(`${baseUrl}/reports/apprentice-ticket-rights${request.query}`, request.token)
}

export function getTicketWithoutTicketRightReport(
  request: AuthenticatedHttpRequest
): Promise<HttpResponse<undefined>> {
  return download(`${baseUrl}/reports/tickets-without-ticket-right${request.query}`, request.token)
}

export function getCouponsStatus(request: AuthenticatedHttpRequest) {
  return get<CouponsStatus>(
    `${baseUrl}/eba/family-members/${request.id}/years/${request.year}/coupons-status`,
    request.token
  )
}

export function getEmployeeTrips(request: AuthenticatedHttpRequest) {
  return get<EmployeeTripsResponse[]>(
    `${baseUrl}/eba/employees/${request.id}/years/${request.year}/trips`,
    request.token
  )
}

export function postFIPTickets(request: AuthenticatedHttpRequest) {
  return post<FipTicket>(`${baseUrl}/eba/employees/${request.id}/fip-tickets`, request.token, request.body || {})
}

export function patchFIPTickets(request: AuthenticatedHttpRequest) {
  const ids = request.id?.split('-')
  return patch<FipTicket>(
    `${baseUrl}/eba/employees/${ids?.pop()}/fip-tickets/${ids?.pop()}`,
    request.token,
    request.body || {}
  )
}

export function postYearlyFee(request: AuthenticatedHttpRequest) {
  return post<YearlyFee>(
    `${baseUrl}/eba/employees/${request.id}/years/${request.year}/yearly-fees`,
    request.token,
    request.body || {}
  )
}

export function postYearlyDisabilityProof(request: AuthenticatedHttpRequest) {
  return post<YearlyDisabilityProof>(
    `${baseUrl}/imports/employees/${request.id}/years/${request.year}/disability-proofs`,
    request.token,
    request.body || {}
  )
}

export function postYearlyCategory(request: AuthenticatedHttpRequest) {
  return post<YearlyCategory>(
    `${baseUrl}/imports/employees/${request.id}/years/${request.year}/categories`,
    request.token,
    request.body || {}
  )
}

export function postYearlyLowIncome(request: AuthenticatedHttpRequest) {
  return post<YearlyLowIncome>(
    `${baseUrl}/eba/family-members/${request.id}/years/${request.year}/low-incomes`,
    request.token,
    request.body || {}
  )
}

export function patchCouponsLimit(request: AuthenticatedHttpRequest) {
  return patch<CouponsLimit>(
    `${baseUrl}/eba/family-members/${request.id}/years/${request.year}/coupons-limit`,
    request.token,
    request.body || {}
  )
}

export function postManualOrder(request: AuthenticatedHttpRequest) {
  return post<ManualOrder>(
    `${baseUrl}/eba/employees/${request.id}/manual-orders`,
    request.token,
    request.body || {}
  )
}

export function patchManualOrder(request: AuthenticatedHttpRequest) {
  const ids = request.id?.split('-')
  return patch<ManualOrder>(
    `${baseUrl}/eba/employees/${ids?.pop()}/manual-orders/${ids?.pop()}`,
    request.token,
    request.body || {}
  )
}

export function fetchDeleteManualOrder(request: AuthenticatedHttpRequest) {
  const ids = request.id?.split('-')
  return fetchDelete<ManualOrder>(
    `${baseUrl}/eba/employees/${ids?.pop()}/manual-orders/${ids?.pop()}`,
    request.token
  )
}

export function postStationPairs(request: AuthenticatedHttpRequest) {
  const ids = request.id?.split('-')
  return post<StationPair>(
    `${baseUrl}/eba/employees/${ids?.pop()}/manual-orders/${ids?.pop()}/station-pairs`,
    request.token,
    request.body || {}
  )
}

export function getStopPlaceRegister(request: HttpRequest) {
  return get<StopPlaces>(
    `${getApiConfig().url}/geocoder/v1/autocomplete?text=${request.id}&boundary.country=NOR&size=100`
  ).then((response) => {
    const stopPlaces: StopPlaces = response.contentType === 'json' ? response.json : { features: [] }
    return filterByCategory(stopPlaces, 'railStation')
  })
}

function filterByCategory(stopPlaces: StopPlaces, categoryFilter: string) {
  return {
    features: stopPlaces.features.filter((stopPlace: { properties: { category: string | string[] } }) =>
      stopPlace.properties.category.includes(categoryFilter)
    ),
  }
}

export function getFamilyMembers(request: AuthenticatedHttpRequest) {
  return get<Array<FamilyMember>>(
    `${baseUrl}/eba/employees/${request.id}/family-members?includeTicketRights=true&includePreferences=true`,
    request.token
  )
}

export function postFamilyMember(request: AuthenticatedHttpRequest) {
  return post<FamilyMember>(
    `${baseUrl}/eba/employees/${request.id}/family-members`,
    request.token,
    request.body || {}
  )
}

export function patchFamilyMember(request: AuthenticatedHttpRequest) {
  return patch<FamilyMember>(`${baseUrl}/eba/family-members/${request.id}`, request.token, request.body || {})
}

export function generateWelcomeLetter(request: AuthenticatedHttpRequest) {
  return post<undefined>(`${baseUrl}/eba/welcome-letter`, request.token, request.body || {})
}

export function getDisclosureDocument(request: AuthenticatedHttpRequest) {
  return get<undefined>(`${baseUrl}/eba/employees/${request.id}/disclosure-document`, request.token)
}

export function getTicketRightHoldersFile(request: AuthenticatedHttpRequest): Promise<HttpResponse<undefined>> {
  return download(`${baseUrl}/eba/ticket-right-holders${request.query}`, request.token)
}

export function getPostalCode(request: AuthenticatedHttpRequest): Promise<HttpResponse<PostalCode>> {
  if (request.query) {
    return get<PostalCode>(`${baseUrl}/eba/postalCode/${request.query}`, request.token)
  }
  return Promise.resolve({ json: {} } as HttpResponse<PostalCode>)
}
